import type { FC } from 'react'
import { useRef, useState } from 'react'

import { MessagePopup, Wizard, WizardStep } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FormikProps, FormikState } from 'formik'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import adminQueries from '@/modules/admin/queries'
import type { AddAssetLocationPayload } from '@/resources/asset-locations'
import assetLocationResources from '@/resources/asset-locations'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { BasicInfo } from './BasicInfo'
import { ConnectedEntities } from './ConnectedEntities'

type FormFields = ReturnType<typeof getInitialValues>

export const NewAssetLocation: FC = () => {
  const { setIsWizardMode } = useAppContext()
  const formRef = useRef<FormikProps<FormFields> | null>(null)

  const translate = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)

  const [saveError, setSaveError] = useState('')
  const addAssetMutation = useMutation(assetLocationResources.add, {
    onSuccess() {
      queryClient.invalidateQueries(commonQueries.assetLocationList(), { refetchType: 'all' })
      queryClient.invalidateQueries(commonQueries.employees(), { refetchType: 'all' })
      queryClient.invalidateQueries(adminQueries.adminEmployeeList(), { refetchType: 'all' })

      navigate(APP_ROUTES.HARDWARE_ASSET_LOCATION_LIST)
    },
  })

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HARDWARE_ASSETS)}>
      <Formik<FormFields>
        initialValues={getInitialValues()}
        enableReinitialize
        innerRef={formRef}
        validateOnBlur={false}
        validate={values => {
          const errors: Partial<Record<keyof FormFields, string>> = {}

          if (!values.name) {
            errors.name = translate('This field is mandatory')
          }
          if (!values.email) {
            errors.email = translate('This field is mandatory')
          }
          if (!values.code) {
            errors.code = translate('This field is mandatory')
          }
          if (!values.customerId) {
            errors.customerId = translate('This field is mandatory')
          }
          if (!values.departmentId) {
            errors.departmentId = translate('This field is mandatory')
          }
          if (!values.customerAddressId) {
            errors.customerAddressId = translate('This field is mandatory')
          }

          return errors
        }}
        onSubmit={formValues => {
          if (!formRef.current?.dirty) return

          const payload: AddAssetLocationPayload = {
            ...formValues,
            customerAddressId: Number(formValues.customerAddressId),
            departmentId: Number(formValues.departmentId),
            customerId: Number(formValues.customerId),
          }
          addAssetMutation.mutate(payload)
        }}
      >
        {({ values }) => {
          const isCurrentTabValid = getIsCurrentTabValid({
            values,
            currentTab: currentStep === 0 ? 'basic-info' : 'connected-entities',
          })

          return (
            <Form>
              <Wizard
                skipButtonTextDefault={<Translation id="Skip" />}
                nextButtonTextDefault={<Translation id="Next Step" />}
                previousButtonTextDefault={<Translation id="Previous Step" />}
                isLoading={addAssetMutation.isLoading}
                onIsWizardModeSet={setIsWizardMode}
                onComplete={() => formRef.current?.submitForm()}
                onStepChange={setCurrentStep}
              >
                <WizardStep name={<Translation id="Basic info" />} isValid={isCurrentTabValid}>
                  <BasicInfo />
                </WizardStep>

                <WizardStep
                  name={<Translation className="capitalize" id="Connected entities" />}
                  isValid={isCurrentTabValid}
                  nextButtonText={<Translation id="Complete" />}
                >
                  <ConnectedEntities selectedCustomerId={Number(values.customerId)} />
                </WizardStep>
              </Wizard>
            </Form>
          )
        }}
      </Formik>

      <MessagePopup
        title={<Translation id="Saving form error" />}
        message={
          <>
            <Translation id="We encountered a problem while submitting the form. Please refresh and try again." />{' '}
            <Translation id="Error" />: {saveError}
          </>
        }
        isOpen={Boolean(saveError)}
        onClose={() => setSaveError('')}
      />
    </Container>
  )
}

export function getIsCurrentTabValid({
  values,
  currentTab,
}: {
  currentTab: string
  values: FormikState<FormFields>['values'] | undefined
}): boolean {
  if (!values) return false
  switch (currentTab) {
    case 'basic-info': {
      return Boolean(values.name && values.code && values.email && values.customerId)
    }
    case 'connected-entities': {
      return Boolean(values.customerAddressId && values.departmentId)
    }
    default:
      return true
  }
}

function getInitialValues() {
  return {
    code: '',
    customerId: '',
    name: '',
    departmentId: '',
    email: '',
    customerAddressId: '',
    accountingCode: '',
    dim1: '',
    dim2: '',
    dim3: '',
    dim4: '',
    description: '',
  }
}
